import { SCHEDULE_SESSION_DISPLAY_TYPE_SPEEDDATE } from 'common-data-handling';
import React, { useCallback, useState } from 'react';
import { DATE_FORMAT_URL } from '../../../data/constants/datetime';
import { useI18n } from '../../../data/hooks/i18nHooks';
import { eventMomentFactory } from '../../../data/misc/eventMomentFactory';
import Grid from '../../elements/Grid';
import EventIcon from '../../elements/icons/EventIcon';
import Link from '../../elements/Link';
import Scrollable from '../../elements/Scrollable';
import LoadLeads from '../../loaders/LoadLeads';
import SandboxedContent from '../../SandboxedContent/SandboxedContent';
import ErrorBoundary from '../../validate/ErrorBoundary';
import Validate from '../../validate/Validate';
import WaitWhileBusy from '../../WaitWhileBusy';
import PokeParticipantScreen from '../PokeParticipantScreen';
import Screen from '../Screen';
import EmailContactButton from './EmailContactButton/EmailContactButton';
import ExtraProfileItem from './ExtraProfileItem';
import UserProfileConnectionHeader from './headers/UserProfileConnectionHeader';
import UserProfileSpeakerHeader from './headers/UserProfileSpeakerHeader';
import LeadRecord from './LeadRecord';
import LinkedinContactButton from './LinkedinContactButton/LinkedinContactButton';
import SpeakerRole from './SpeakerRole/SpeakerRole';
import { useParams } from 'react-router-dom';

const UserProfile = (props) => {
    const { eventId, event, user, canBeInvited, inviteURL, isSpeaker = false, returnURL, speakerRoles, programItem } = props,
        { sponsorId } = useParams(),
        i18n = useI18n(),
        [showPokeScreen, setShowPokeScreen] = useState(false);

    const handleOpenProgramItem = useCallback(
        (programItem) => {
            app.history.push(['/events', eventId, 'schedule', eventMomentFactory(event, programItem.start_local).format(DATE_FORMAT_URL), programItem.id].join('/'));
        },
        [eventId]
    );

    const handleCloseSubScreen = useCallback(() => {
        setShowPokeScreen(false);
    }, []);

    const handlePokeUser = useCallback(() => {
        setShowPokeScreen(true);
    }, []);

    if (showPokeScreen) {
        return <PokeParticipantScreen event={event} user={user} i18n={i18n} onClose={handleCloseSubScreen} />;
    }

    // console.log(user);

    let header;
    if (isSpeaker) {
        header = <UserProfileSpeakerHeader event={event} user={user} returnURL={returnURL} canBeInvited={canBeInvited} inviteURL={inviteURL} onPokeUser={handlePokeUser} />;
    } else {
        header = <UserProfileConnectionHeader event={event} user={user} canBeInvited={canBeInvited} inviteURL={inviteURL} returnURL={returnURL} onPokeUser={handlePokeUser} />;
    }

    return (
        <Screen className="UserProfileScreen" event={event} header={header}>
            <Scrollable>
                <LeadRecord className="content" event={event} user={user} sponsorId={sponsorId} editable />
                <div className="marginTop">
                    <SandboxedContent content={user.description} fitHeightToContents />
                </div>
                {user.events_connected?.length ? (
                    <div className="events">
                        <h1>{i18n['label.contacted_at']}</h1>
                        <Grid className="valignTop" columns={5} useEndPadding="useEndPadding">
                            {user.events_connected.map((event, i) => (
                                <Link className="eventIcon" key={i} href={['/events', event.id, 'participants', user.id, '-'].join('/')}>
                                    <EventIcon event={event} />
                                </Link>
                            ))}
                        </Grid>
                    </div>
                ) : null}
                <div className="content">
                    {(user.extra_profile_data || []).map((item, i) => (
                        <ExtraProfileItem {...props} item={item} key={i} />
                    ))}
                </div>

                {speakerRoles ? (
                    <ul className="list whitebg" style={{ marginRight: 0 }}>
                        {speakerRoles.map((sr, i) =>
                            !programItem || programItem.display_type != SCHEDULE_SESSION_DISPLAY_TYPE_SPEEDDATE || programItem.id != sr.programItem.id ? (
                                <SpeakerRole style={{ marginRight: 0, paddingRight: 0 }} {...props} sr={sr} key={i} onOpenProgramItem={handleOpenProgramItem} />
                            ) : null
                        )}
                    </ul>
                ) : null}

                <div className="content">
                    <div className="unmargin-right">
                        <LinkedinContactButton className="marginBottom" user={user} />
                        <EmailContactButton className="marginBottom" user={user} />
                    </div>
                </div>
            </Scrollable>
        </Screen>
    );
};

export default (props) => {
    const { eventId } = useParams();
    return (
        <ErrorBoundary altRoute="..">
            <LoadLeads eventId={eventId}>
                <WaitWhileBusy>
                    <Validate
                        validate={(state) => {
                            if (!props.user) {
                                throw new Error('Unable to open user ', props);
                            }
                        }}>
                        <UserProfile {...props} />
                    </Validate>
                </WaitWhileBusy>
            </LoadLeads>
        </ErrorBoundary>
    );
};
